import type { Router } from 'vue-router'
import { reactive, ref, toRaw, unref, watch, watchEffect, type WatchStopHandle } from 'vue'
import type { MaybeRef } from '@vueuse/core'
import type {
  LocationRoutes,
  UpdateLocationOnGetRoutesResponse,
  UpdateLocationOnRestore,
  UpdateLocationOnUpdate,
  UpdateLocationPitcherEvent,
} from '@sdk/interfaces'
import useLocationAPI from '@lib/composables/useLocationAPI'
import useLocationManipulation from '@lib/composables/useLocationManipulation'
import useLocationListeners from '@lib/composables/useLocationListeners'

export interface UseLocationPayload {
  router: Router
  app: string
  isReady?: MaybeRef<boolean>
  urlMode?: 'hash' | 'history'
  isRoot?: boolean
  selfRegister?: boolean
  isDebug?: boolean
}

const EventListenerType = {
  popstate: 'popstate',
} as const

const debug = (app: string, routes: LocationRoutes, relations: Record<string, string>) => {
  Object.assign(window, { locationModule: { routes, relations } })

  watchEffect(() => {
    JSON.stringify(routes)

    console.group(app)
    for (const name in routes) {
      console.log(`[${name}]`, toRaw(routes[name]))
    }

    console.log('Relations', toRaw(relations))
    console.groupEnd()
  })
}

const isAnyRouteRestoring = (routes: LocationRoutes, newRoutes: UpdateLocationOnUpdate['routes']) => {
  for (const app in newRoutes) {
    if (routes[app]?.is_restoring === true) {
      return true
    }
  }

  return false
}

export const ROOT_APP = 'root'

export default function useLocation({
  app,
  router,
  isReady = true,
  isRoot = false,
  selfRegister = false,
  isDebug = false,
  urlMode = 'history',
}: UseLocationPayload) {
  const actionsQueue = ref<UpdateLocationPitcherEvent[]>([])
  const routes: LocationRoutes = reactive({})
  const relations: Record<string, string> = reactive({})

  const { removeListeners, initListeners, sendToRoot } = useLocationListeners(async (body) => {
    if (!unref(isReady)) {
      addActionToQueue(body)
      return
    }

    await executeAction(body)
  }, isRoot)

  const { broadcast, internalRouter, globalRouter, buildUrl } = useLocationAPI({
    app,
    isRoot,
    router,
    urlMode,
    sendToRoot,
  })

  const {
    addActionToQueue,
    registerLocation,
    unregisterLocation,
    recoverRouteFromLocations,
    isSameRouteAsStored,
    isSameRoute,
    isAppMounted,
    isAppRestored,
    isPathRestored,
  } = useLocationManipulation({
    app,
    relations,
    routes,
    actionsQueue,
  })

  const updateRootUrl = async () => {
    if (!isRoot) {
      throw new Error('"updateRootUrl" fn should be called only from root app')
    }

    const { path, query } = recoverRouteFromLocations(selfRegister ? ROOT_APP : app)
    const newUrl = buildUrl(path, query)
    const currentUrl = globalRouter.getPathWithSearch()

    if (newUrl === currentUrl) return

    await internalRouter.replace(newUrl)
  }

  const watchQueue = () =>
    watch(
      [isReady, () => actionsQueue.value.length],
      async () => {
        if (!unref(isReady)) return

        while (actionsQueue.value.length) {
          const body = actionsQueue.value.shift()
          if (!body) continue

          await executeAction(body)
        }
      },
      { immediate: true },
    )

  const watchCurrentRoute = () =>
    watch(
      [internalRouter.path, internalRouter.query],
      async () => {
        if (!isAppMounted.value) {
          return
        } else if (selfRegister) {
          const { path, query } = recoverRouteFromLocations(selfRegister ? ROOT_APP : app)
          const buildLocationUrl = buildUrl(path, query)
          const buildGlobalUrl = globalRouter.getPathWithSearch()
          const isSameGlobalAsRoutes = isSameRoute(
            {
              path: globalRouter.getPath(),
              query: globalRouter.getSearchAsObject(),
            },
            {
              path: routes[app].path,
              query: routes[app].query,
            },
          )

          if (isSameGlobalAsRoutes || buildLocationUrl === buildGlobalUrl) {
            return
          }
        } else if (isSameRouteAsStored(internalRouter)) {
          return
        }

        await broadcast.update({
          [app]: {
            path: internalRouter.path.value,
            query: internalRouter.query.value,
          },
        })
      },
      { immediate: true, deep: true },
    )

  const restoreLocation = async (newRoutes: UpdateLocationOnRestore['routes']) => {
    updateRoutes(newRoutes)

    await internalRouter.push({
      path: newRoutes[app].path,
      query: newRoutes[app].query,
    })

    await broadcast.restoreCompleted(internalRouter.path.value, internalRouter.query.value)
  }

  const updateRoutes = (newRoutes: UpdateLocationOnUpdate['routes']) => {
    let isChanged = false

    const routeNames = Object.keys(routes)
    const newRouteNames = Object.keys(newRoutes)
    const notUsedRoutes = routeNames.filter((route) => !newRouteNames.includes(route))

    for (const appName of notUsedRoutes) {
      if (routes[appName] && !routes[appName].is_inactive) {
        routes[appName].is_inactive = true
        isChanged = true
      }
    }

    for (const appName of newRouteNames) {
      const currentRoute = routes[appName]
      const newRoute = newRoutes[appName]

      if (currentRoute) {
        routes[appName].is_inactive = false
      }

      if (!currentRoute) {
        isChanged = true

        routes[appName] = {
          path: newRoute.path ?? '',
          query: newRoute.query ?? {},
          is_restoring: true,
          is_mounted: false,
          is_inactive: false,
        }
      } else if (!isSameRoute(currentRoute, newRoute)) {
        isChanged = true
        routes[appName].path = newRoutes[appName].path
        routes[appName].query = newRoutes[appName].query
      }
    }

    return isChanged
  }

  const popStateListener = async () => {
    const localLocation: UpdateLocationOnRestore['routes'] = {
      [relations[app]]: {
        path: globalRouter.getPath(),
        query: globalRouter.getSearchAsObject(),
        is_restoring: true,
        is_inactive: false,
      },
    }

    await broadcast.restore(localLocation)
  }

  const restoreRootLocation = () => {
    let unwatch: WatchStopHandle | null = null

    unwatch = watchEffect(async () => {
      const appName = selfRegister ? app : relations[app]

      if (!unref(isReady) || !routes[appName]?.is_mounted) {
        return
      }

      unwatch?.()

      const newRoutes: UpdateLocationOnRestore['routes'] = {
        [appName]: {
          path: globalRouter.getPath(),
          query: globalRouter.getSearchAsObject(),
          is_restoring: true,
          is_inactive: false,
        },
      }

      for (const appName in newRoutes) {
        Object.assign(routes[appName], newRoutes[appName])
      }

      await broadcast.restore(newRoutes)

      isPathRestored.value = true

      if (selfRegister) {
        watchCurrentRoute()
      }
    })
  }

  const update = async (newRoutes: UpdateLocationOnUpdate['routes']) => {
    if (isRoot) {
      if (isAnyRouteRestoring(routes, newRoutes) || !isPathRestored.value) return
    }

    const isChanged = updateRoutes(newRoutes)

    if (isChanged && isRoot) {
      await updateRootUrl()
    }
  }

  const onGetRoutesResponse = async (body: UpdateLocationOnGetRoutesResponse) => {
    Object.assign(routes, body.routes)

    try {
      if (!routes[app]) {
        throw new Error(`The parent app needs to register its child before app "${app}" iframe can load.`)
      }

      const { path, query } = routes[app]

      await internalRouter.push({ path, query })

      // Check if redirects occurred during push
      if (!isSameRouteAsStored(internalRouter)) {
        await broadcast.update({
          [app]: {
            path: internalRouter.path.value,
            query: internalRouter.query.value,
          },
        })
      }
    } finally {
      await broadcast.mounted()
    }
  }

  const executeAction = async (body: UpdateLocationPitcherEvent) => {
    switch (body.action) {
      case 'unregister':
        unregisterLocation(body.app)
        break
      case 'restore':
        if (!(app in body.routes)) {
          break
        }

        await restoreLocation(body.routes)
        break
      case 'update':
        await update(body.routes)
        break
      case 'register':
        registerLocation({ child_app: body.child_app, parent_app: body.parent_app, path: body.path, query: body.query })
        break
      case 'restore_completed':
        if (!(body.app in routes)) break

        routes[body.app].is_restoring = false

        await update({
          [body.app]: {
            path: body.path,
            query: body.query,
          },
        })

        break
      case 'mounted':
        if (!(body.app in routes)) break

        routes[body.app].is_mounted = true
        break
      case 'get_routes_request':
        if (isRoot) {
          await broadcast.getRoutesResponse(body.app, routes)
        }

        break
      case 'get_routes_response':
        if (body.app !== app) {
          break
        }

        await onGetRoutesResponse(body)
        break
    }
  }

  const mount = async () => {
    await initListeners()

    if (isDebug) {
      debug(app, routes, relations)
    }

    if (selfRegister) {
      registerLocation({
        child_app: app,
        parent_app: ROOT_APP,
        path: globalRouter.getPath(),
        query: globalRouter.getSearchAsObject(),
      })
    }

    if (isRoot) {
      restoreRootLocation()
      addEventListener(EventListenerType.popstate, popStateListener)
    } else {
      watchCurrentRoute()
    }

    watchQueue()

    if (isRoot && selfRegister) {
      routes[app].is_mounted = true
      return
    }

    await broadcast.mount()
  }

  const unmount = async () => {
    if (isRoot) {
      removeEventListener(EventListenerType.popstate, popStateListener)
    }

    await removeListeners()
  }

  mount()

  return {
    registerApp: broadcast.register,
    unregisterApp: broadcast.unregister,
    routes,
    relations,
    unmount,
    update,
    isPathRestored,
    isAppMounted,
    isAppRestored,
    app,
  }
}
